import { DotsThreeVerticalIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Text } from '../../../components';
import { Color, Style } from '../../../constants';

type MenuItem = {
  key: string;
  label: string;
  icon?: React.ComponentType<{ size: number; color?: string }>;
  danger?: boolean;
  onClick: () => void;
};

type Props = {
  items: MenuItem[];
};

export default function CardActionsMenu({ items }: Props) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener('click', onClickOutside, true);
    return () => window.removeEventListener('click', onClickOutside, true);
  }, [open]);

  return (
    <html.div ref={ref} style={Style.positionRelative}>
      <html.button
        style={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-label="Actions"
      >
        <DotsThreeVerticalIcon size={20} color={Color.neutral80} />
      </html.button>
      {open && (
        <html.div style={styles.menu}>
          {items.map(({ key, label, icon: Icon, danger, onClick }) => (
            <html.button
              key={key}
              style={styles.item}
              onClick={() => {
                setOpen(false);
                onClick();
              }}
            >
              {Icon && (
                <Icon
                  size={18}
                  color={danger ? Color.accent : Color.neutral80}
                />
              )}
              <Text
                typeface="body.regular"
                color={danger ? 'accent' : 'neutral100'}
              >
                {label}
              </Text>
            </html.button>
          ))}
        </html.div>
      )}
    </html.div>
  );
}

const styles = css.create({
  trigger: {
    display: 'flex',
    borderWidth: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 4,
  },
  menu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    minWidth: 270,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: color.neutral0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    boxShadow: '0px 4px 22px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    zIndex: 200,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingBlock: 10,
    paddingInline: 12,
    borderWidth: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.15s',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
});
